import { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  associateSignin,
  requestOtp,
  requestPasswordResetOtp,
  confirmPasswordReset,
} from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { useCooldown } from '@shared/hooks/use-cooldown';
import { usePublicRedirect, ME_QUERY_KEY, roleHome } from '@shared/hooks/use-me';
import { useAuthStore } from '@shared/stores/auth-store';
import { getTokenStore } from '@shared/api/token-store';
import { validateEmail } from '@shared/utils/validation';
import { EyeIcon, EyeOffIcon } from '@/components/layout/icons';
import { AuthShell } from '@/features/auth/web/auth-shell';

export function AssociateLoginPage() {
  const [mode, setMode] = useState<'password' | 'otp'>('password');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password states
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState<'email' | 'reset'>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailError, setForgotEmailError] = useState<string | null>(null);
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotValidationError, setForgotValidationError] = useState<string | null>(null);

  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  const { secondsLeft, active: onCooldown, start: startCooldown } = useCooldown(30);
  const { target, waiting } = usePublicRedirect();

  const signinMutation = useMutation({
    mutationFn: () => associateSignin({ email: email.trim(), password }),
    onSuccess: (session) => {
      getTokenStore().setAccessToken(session.accessToken, 'associate');
      setSession(session.user);
      qc.setQueryData(ME_QUERY_KEY, {
        ...session.user,
        avatarUrl: null,
        emailVerifiedAt: null,
        lastLoginAt: null,
        createdAt: new Date().toISOString(),
      });
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate(roleHome(session.user.role), { replace: true });
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err));
    },
  });

  const otpMutation = useMutation({
    mutationFn: (e: string) => requestOtp(e, 'ASSOCIATE'),
    onSuccess: () => navigate(`/otp?email=${encodeURIComponent(email.trim())}&as=associate`),
    onSettled: () => startCooldown(),
    onError: (err) => {
      toast.error(apiErrorMessage(err));
    },
  });

  // Forgot Password cooldown & mutations
  const {
    secondsLeft: forgotSecondsLeft,
    active: forgotCooldownActive,
    start: startForgotCooldown,
  } = useCooldown(30);

  const forgotOtpMutation = useMutation({
    mutationFn: (e: string) => requestPasswordResetOtp(e),
    onSuccess: () => {
      setForgotStep('reset');
      setForgotValidationError(null);
      toast.success(
        `6-digit password reset OTP sent to ${forgotEmail.trim().toLowerCase()}. It is valid for 10 minutes.`,
      );
    },
    onSettled: () => startForgotCooldown(),
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  const confirmResetMutation = useMutation({
    mutationFn: () =>
      confirmPasswordReset({
        email: forgotEmail.trim(),
        otpCode: forgotOtp.trim(),
        newPassword,
      }),
    onSuccess: () => {
      toast.success('Password reset successfully! Please log in with your new password.');
      setIsForgotMode(false);
      setMode('password');
      setEmail(forgotEmail.trim());
      setPassword('');
      setForgotOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setForgotValidationError(null);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  if (waiting) {
    return (
      <AuthShell>
        <div className="text-center text-sm text-gray-500">Loading…</div>
      </AuthShell>
    );
  }
  if (target) return <Navigate to={target} replace />;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailTouched(true);
    const result = validateEmail(email);
    if (!result.isValid) {
      setEmailError(result.error);
      return;
    }
    setEmailError(null);

    const emailVal = email.trim();
    if (mode === 'otp') {
      if (onCooldown || otpMutation.isPending) return;
      otpMutation.mutate(emailVal);
    } else {
      if (signinMutation.isPending || !password) return;
      signinMutation.mutate();
    }
  }

  function handleEmailChange(val: string) {
    setEmail(val);
    if (emailTouched) {
      const result = validateEmail(val);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  function handleEmailBlur() {
    setEmailTouched(true);
    if (email.trim()) {
      const result = validateEmail(email);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  function onForgotEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = validateEmail(forgotEmail);
    if (!result.isValid) {
      setForgotEmailError(result.error);
      if (result.error) toast.error(result.error);
      return;
    }
    setForgotEmailError(null);
    if (forgotCooldownActive || forgotOtpMutation.isPending) return;
    forgotOtpMutation.mutate(forgotEmail.trim());
  }

  function onForgotResetSubmit(e: React.FormEvent) {
    e.preventDefault();
    setForgotValidationError(null);

    if (!forgotOtp.trim() || forgotOtp.trim().length !== 6) {
      const err = 'Please enter a valid 6-digit OTP code.';
      setForgotValidationError(err);
      toast.error(err);
      return;
    }

    if (newPassword.length < 8) {
      const err = 'Password must be at least 8 characters long.';
      setForgotValidationError(err);
      toast.error(err);
      return;
    }

    if (newPassword !== confirmPassword) {
      const err = 'Passwords do not match.';
      setForgotValidationError(err);
      toast.error(err);
      return;
    }

    confirmResetMutation.mutate();
  }

  return (
    <AuthShell>
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            {isForgotMode ? 'Reset Associate Password' : 'Associate Log In'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isForgotMode
              ? forgotStep === 'email'
                ? 'Enter your registered email to receive a password reset OTP'
                : `Enter the 6-digit code sent to ${forgotEmail.trim().toLowerCase()} and set your new password`
              : 'Access your EcRegi partner workspace'}
          </p>
        </div>

        {isForgotMode ? (
          <div className="space-y-4">
            {forgotStep === 'email' ? (
              <form className="space-y-3.5" onSubmit={onForgotEmailSubmit} noValidate>
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">Email address</label>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Enter your registered email"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                      if (forgotEmailError) setForgotEmailError(null);
                    }}
                    className={forgotEmailError ? 'border-red-500' : undefined}
                    required
                    autoFocus
                  />
                  {forgotEmailError && (
                    <p className="mt-1 text-xs font-medium text-red-600">{forgotEmailError}</p>
                  )}
                </div>

                {forgotOtpMutation.isError && (
                  <p className="text-xs text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200">
                    {apiErrorMessage(forgotOtpMutation.error)}
                  </p>
                )}

                <Button
                  type="submit"
                  block
                  disabled={forgotOtpMutation.isPending || forgotCooldownActive}
                >
                  {forgotOtpMutation.isPending
                    ? 'Sending OTP…'
                    : forgotCooldownActive
                      ? `Resend in ${forgotSecondsLeft}s`
                      : 'Send Reset OTP'}
                </Button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotMode(false);
                      setForgotValidationError(null);
                    }}
                    className="text-xs text-gray-600 hover:text-gray-900 underline underline-offset-2"
                  >
                    Back to Log In
                  </button>
                </div>
              </form>
            ) : (
              <form className="space-y-3.5" onSubmit={onForgotResetSubmit} noValidate>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-medium text-gray-700">6-Digit OTP</label>
                    <button
                      type="button"
                      onClick={() => setForgotStep('email')}
                      className="text-xs text-brand-700 hover:underline"
                    >
                      Change email
                    </button>
                  </div>
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="Enter 6-digit OTP"
                    value={forgotOtp}
                    onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    autoFocus
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">New Password</label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOffIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOffIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {forgotValidationError && (
                  <p className="text-xs font-medium text-red-600">{forgotValidationError}</p>
                )}

                {confirmResetMutation.isError && (
                  <p className="text-xs text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200">
                    {apiErrorMessage(confirmResetMutation.error)}
                  </p>
                )}

                <Button type="submit" block disabled={confirmResetMutation.isPending}>
                  {confirmResetMutation.isPending ? 'Setting New Password…' : 'Set New Password'}
                </Button>

                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    disabled={forgotCooldownActive || forgotOtpMutation.isPending}
                    onClick={() => forgotOtpMutation.mutate(forgotEmail.trim())}
                    className="text-xs text-brand-700 hover:underline disabled:text-gray-400"
                  >
                    {forgotCooldownActive ? `Resend in ${forgotSecondsLeft}s` : 'Resend OTP'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotMode(false);
                      setForgotValidationError(null);
                    }}
                    className="text-xs text-gray-600 hover:text-gray-900 underline underline-offset-2"
                  >
                    Back to Log In
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-700">Email address</label>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Enter your registered email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                className={
                  emailError
                    ? 'border-red-500 focus:border-red-600 focus:ring-red-500/30'
                    : undefined
                }
                required
                autoFocus
              />
              {emailError && (
                <p className="mt-1 text-xs font-medium text-red-600">{emailError}</p>
              )}
            </div>

            {mode === 'password' && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-medium text-gray-700">Password</label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotMode(true);
                      setForgotStep('email');
                      setForgotEmail(email.trim());
                      setForgotEmailError(null);
                      setForgotValidationError(null);
                    }}
                    className="text-xs text-brand-700 hover:text-brand-800 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {mode === 'password' ? (
              signinMutation.isError && (
                <p className="text-sm text-red-600">{apiErrorMessage(signinMutation.error)}</p>
              )
            ) : (
              otpMutation.isError && (
                <p className="text-sm text-red-600">{apiErrorMessage(otpMutation.error)}</p>
              )
            )}

            {mode === 'password' ? (
              <Button type="submit" block disabled={signinMutation.isPending}>
                {signinMutation.isPending ? 'Logging in…' : 'Log In with Password'}
              </Button>
            ) : (
              <Button type="submit" block disabled={otpMutation.isPending || onCooldown}>
                {otpMutation.isPending
                  ? 'Sending…'
                  : onCooldown
                    ? `Try again in ${secondsLeft}s`
                    : 'Send OTP'}
              </Button>
            )}

            <div className="text-center">
              <button
                type="button"
                className="text-xs text-brand-700 underline underline-offset-2 hover:text-brand-800"
                onClick={() => setMode(mode === 'password' ? 'otp' : 'password')}
              >
                {mode === 'password' ? 'Use OTP instead' : 'Use password instead'}
              </button>
            </div>

            <p className="pt-2 text-center text-xs text-gray-500 border-t border-gray-100">
              Don&rsquo;t have a partner account?{' '}
              <Link to="/associate/join" className="text-brand-700 underline underline-offset-2">
                Join as Associate
              </Link>
            </p>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
